/**
 * A grounded answer, laid out like a short paper: explanatory paragraphs and
 * bullets with small `[n]` markers, then a numbered **References** list after
 * the answer. A marker's tooltip shows the quoted passage; a marker or a
 * reference opens the source document at the cited page.
 */

import { Fragment, type ReactNode } from "react";
import { Box, Code, ScrollArea, Stack, Table, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { type Citation, citationLabel, useOpenCitation } from "@/features/solutions/shared";
import { type Block, type Inline, parseAnswer } from "./answerMarkdown";

function CiteMarker({ citation }: { citation: Citation }) {
  const { open, opening } = useOpenCitation();
  const label = citationLabel(citation);
  return (
    <Tooltip
      label={
        <Stack gap={2}>
          <Text size="xs" fw={600}>
            {label}
          </Text>
          {citation.snippet && <Text size="xs">{citation.snippet}</Text>}
        </Stack>
      }
      multiline
      w={360}
      withArrow
      openDelay={150}
    >
      <UnstyledButton
        component="button"
        type="button"
        onClick={() => void open(citation)}
        disabled={opening}
        aria-label={`Open source: ${label}`}
        data-testid="cite-marker"
        fz="xs"
        fw={600}
        c="var(--mantine-primary-color-filled)"
        px={1}
        style={{ verticalAlign: "baseline", cursor: "pointer" }}
      >
        [{citation.n}]
      </UnstyledButton>
    </Tooltip>
  );
}

function InlineText({ inline, byN }: { inline: Inline[]; byN: Map<number, Citation> }) {
  return (
    <>
      {inline.map((token, i) => {
        if (token.kind === "text") return <Fragment key={i}>{token.text}</Fragment>;
        if (token.kind === "bold") return <strong key={i}>{token.text}</strong>;
        if (token.kind === "code") return <Code key={i}>{token.text}</Code>;
        const citation = byN.get(token.n);
        return citation ? <CiteMarker key={i} citation={citation} /> : null;
      })}
    </>
  );
}

function renderBlock(block: Block, i: number, byN: Map<number, Citation>): ReactNode {
  if (block.kind === "heading") {
    return (
      <Text key={i} size="sm" fw={700} mt={4}>
        <InlineText inline={block.inline} byN={byN} />
      </Text>
    );
  }
  if (block.kind === "table") {
    return (
      <ScrollArea key={i} type="auto" offsetScrollbars>
        <Table withTableBorder withColumnBorders striped fz="sm" data-testid="answer-table">
          <Table.Thead>
            <Table.Tr>
              {block.header.map((cell, j) => (
                <Table.Th key={j}>
                  <InlineText inline={cell} byN={byN} />
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {block.rows.map((row, r) => (
              <Table.Tr key={r}>
                {row.map((cell, j) => (
                  <Table.Td key={j}>
                    <InlineText inline={cell} byN={byN} />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    );
  }
  if (block.kind === "paragraph") {
    return (
      <Text key={i} size="sm" lh={1.65}>
        <InlineText inline={block.inline} byN={byN} />
      </Text>
    );
  }
  return (
    <Box
      key={i}
      component={block.ordered ? "ol" : "ul"}
      m={0}
      pl={22}
      style={{ display: "flex", flexDirection: "column", gap: 4 }}
    >
      {block.items.map((item, j) => (
        <Text
          key={j}
          component="li"
          size="sm"
          lh={1.6}
          ml={item.depth ? 18 : 0}
          style={item.depth ? { listStyleType: "circle" } : undefined}
        >
          <InlineText inline={item.inline} byN={byN} />
        </Text>
      ))}
    </Box>
  );
}

function References({ citations }: { citations: Citation[] }) {
  const { open, opening } = useOpenCitation();
  return (
    <Stack gap={6} pt={10} data-testid="answer-references">
      <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.4 }}>
        References
      </Text>
      {citations.map((c) => (
        <Box key={c.n} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <Text size="xs" fw={600} c="dimmed" style={{ minWidth: 22 }}>
            [{c.n}]
          </Text>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <UnstyledButton
              component="button"
              type="button"
              onClick={() => void open(c)}
              disabled={opening}
              aria-label={`Open source: ${citationLabel(c)}`}
              data-testid="reference"
              fz="sm"
              c="var(--mantine-primary-color-filled)"
              style={{ textAlign: "left", cursor: "pointer" }}
            >
              {citationLabel(c)}
            </UnstyledButton>
            {c.snippet && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                “{c.snippet}”
              </Text>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export function AnswerBody({ content, citations }: { content: string; citations: Citation[] }) {
  const byN = new Map(citations.map((c) => [c.n, c]));
  const blocks = parseAnswer(content, new Set(byN.keys()));
  const references = [...byN.values()].sort((a, b) => a.n - b.n);
  return (
    <Stack gap={8}>
      {blocks.map((block, i) => renderBlock(block, i, byN))}
      {references.length > 0 && <References citations={references} />}
    </Stack>
  );
}
